import { downloadTicketsCsv, getErrorMessageFromPossibleBlob, usePaginateCarts } from '@gozoki/api'
import type { CartStatus, CartsWithReportUrl } from '@gozoki/api-types'
import { Button, Chip, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import type { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Eye, User } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useState } from 'react'
import Page from '../../components/Page'
import { useToast } from '../../components/communication/Toast'
import SimpleDataGrid from '../../components/containers/SimpleDataGrid'
import { CustomerInfoDialog } from '../../components/routes/carts/CustomerInfoDialog'
import { StoresPicker } from '../../utils/dashboard/storesPicker'
import { fonts } from '../../utils/theme/fonts.theme'

import { withResourcePermission } from '../../components/RequireResourcePermission'
export const Route = createFileRoute('/_authenticated/carts')({
  component: withResourcePermission('carts', 'read')(CartsPage),
})

const CartStatus = ({ cart }: { cart: CartsWithReportUrl }) => {
  let title = 'En attente de validation'
  if (cart.status === 'validated') {
    title = 'Validé'
  }
  if (cart.status === 'cancelled') {
    title = 'Annulé'
  }
  if (cart.status === 'stolen') {
    title = 'Volé'
  }

  let color: 'default' | 'success' | 'info' | 'warning' | 'error' | 'primary' | 'secondary' =
    'primary'
  if (cart.status === 'validated') {
    color = 'success'
  }
  if (cart.status === 'cancelled') {
    color = 'warning'
  }
  if (cart.status === 'stolen') {
    color = 'error'
  }

  return <Chip style={{ height: 35 }} label={title} color={color} />
}

const cartsColumnDef = ({
  onCustomerInfoClick,
}: {
  onCustomerInfoClick: (cartId: number) => void
}) => {
  const gridColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'N° de panier',
      width: 200,
    },
    {
      field: 'createdAt',
      headerName: 'Date et heure de création',
      sortable: true,
      valueFormatter: (params) =>
        DateTime.fromISO(params.value as string)
          .setZone('Europe/Paris')
          .setLocale('fr')
          .toFormat("'le' d MMMM 'à' HH'h'mm'm'ss's'"),
      width: 260,
    },
    {
      field: 'articles',
      headerName: "Nb d'articles",
      valueFormatter: (params) => params.value.length,
      width: 180,
    },
    {
      field: 'amountTotal',
      flex: 1,
      headerName: 'Prix total',
      valueFormatter: (params) => `${params.value} €`,
      width: 180,
    },
    {
      field: 'userId',
      headerName: 'Utilisateur application ?',
      sortable: false,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.userId != null ? (
          <Tooltip title="Voir la fiche client">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onCustomerInfoClick(params.row.id as number)
              }}
              aria-label="Voir la fiche client"
            >
              <User size={18} />
            </IconButton>
          </Tooltip>
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Statut',
      renderCell: (params) => <CartStatus cart={params.row as CartsWithReportUrl} />,
      width: 200,
    },
    {
      field: 'reportUrl',
      headerName: '',
      renderCell: (params) => (
        <a
          target="_blank"
          href={`${params.row.reportUrl}`}
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="outlined" color="success" style={{ width: '100%' }}>
            <Eye />
            &nbsp;Voir la facture
          </Button>
        </a>
      ),
      width: 200,
    },
  ]
  return gridColumns
}

type CartsTab = 'all' | 'pending'

function CartsPage() {
  const [currentStore, setCurrentStore] = useState<number | null>(null)

  const [tab, setTab] = useState<CartsTab>('all')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState(20)
  const [startDate, setStartDate] = useState<DateTime | null>(null)
  const [endDate, setEndDate] = useState<DateTime | null>(null)
  const [selectedCartId, setSelectedCartId] = useState<number | null>(null)
  const [Toast, showToast] = useToast()
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }])

  const sortBy = sortModel[0]?.field === 'createdAt' ? 'created_at' : 'updated_at'
  const sortDirection = sortModel[0]?.sort ?? 'desc'
  const cartStatus: CartStatus | null = tab === 'pending' ? 'pending' : null

  const { data: carts, isLoading } = usePaginateCarts(
    currentStore ?? 0,
    page + 1,
    pageSize,
    startDate ? startDate.toISO() : null,
    endDate ? endDate.toISO() : null,
    cartStatus,
    sortBy,
    sortDirection,
  )

  const { mutate: mutateDownloadAuditFile, isPending: isLoadingAudit } = useMutation({
    mutationFn: downloadTicketsCsv,
    onError: async (error) => {
      const errorMessage = await getErrorMessageFromPossibleBlob(error)

      showToast({ message: errorMessage, severity: 'error' })
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `liens-factures_${startDate?.toFormat('yyyy-MM-dd')}_${endDate?.toFormat(
          'yyyy-MM-dd',
        )}.xlsx`,
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    },
  })

  const handleDownloadAllTickets = useCallback(() => {
    const usedStartDate = startDate?.toISODate() ?? DateTime.now().minus({ days: 31 }).toISODate()
    const usedEndDate = endDate?.toISODate() ?? DateTime.now().toISODate()
    mutateDownloadAuditFile({
      dateEnd: usedEndDate,
      dateStart: usedStartDate,
    })
  }, [mutateDownloadAuditFile, startDate, endDate])

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex', paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Paniers
        </Typography>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', flex: 3, padding: '16px' }}>
          <div style={{ flex: 1 }}>
            <StoresPicker onlyBoutiques defaultStore={3} setUniqueStore={setCurrentStore} />
          </div>
        </div>
      </Paper>

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: '12px', padding: '16px' }}>
          <Typography>Du</Typography>
          <DateTimePicker
            label="Date de départ"
            value={startDate}
            onChange={(date: DateTime | null) => setStartDate(date || null)}
            maxDate={endDate ?? DateTime.now()}
            ampm={false}
            format="dd/MM/yyyy HH:mm"
          />
          <Typography>au</Typography>
          <DateTimePicker
            label="Date de fin"
            value={endDate}
            onChange={(date: DateTime | null) => setEndDate(date || null)}
            disabled={startDate === null}
            minDate={startDate ? startDate.plus({ minute: 1 }) : undefined}
            maxDate={DateTime.now()}
            ampm={false}
            format="dd/MM/yyyy HH:mm"
          />
          <Button
            variant="contained"
            color="secondary"
            style={{ marginLeft: 16 }}
            onClick={() => {
              setStartDate(null)
              setEndDate(null)
            }}
            disabled={startDate === null && endDate === null}
          >
            Réinitialiser le filtre
          </Button>
          <div style={{ flexGrow: 1 }}>
            <Button
              variant="contained"
              color="info"
              onClick={handleDownloadAllTickets}
              disabled={isLoadingAudit}
            >
              Exports des tickets
            </Button>
          </div>
        </div>
        <Tabs
          value={tab}
          onChange={(_, value: CartsTab) => {
            setTab(value)
            setPage(0)
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab value="all" label="Tous" />
          <Tab value="pending" label="En attente" />
        </Tabs>
        <SimpleDataGrid
          columns={cartsColumnDef({ onCustomerInfoClick: setSelectedCartId })}
          rows={
            carts?.data?.map((c) => ({
              ...c,
              articles: c.articles ?? [],
            })) ?? []
          }
          loading={isLoading}
          rowCount={carts?.meta.total ?? 0}
          pageSizeOptions={[20, 50]}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            // Keep only the first sort (single-column sorting)
            setSortModel(model.slice(0, 1))
          }}
          initialState={{
            pagination: {
              paginationModel: { page, pageSize },
            },
          }}
          onPaginationModelChange={(params) => {
            setPage(params.page)
            setPageSize(params.pageSize)
          }}
          rowSelection={false}
          disableRowSelectionOnClick
          checkboxSelection={false}
        />
      </Paper>
      <CustomerInfoDialog
        cartId={selectedCartId}
        open={selectedCartId != null}
        onClose={() => setSelectedCartId(null)}
      />
    </Page>
  )
}
